<script setup>
import { computed, ref } from "vue";
import { Modal } from "@Components/Modal";
import { PrimaryButton } from "@Components/Button";
import { useStore } from "@/Stores/templates";
import LoadingAnimation from "@Components/Animation/Loading.vue";
import ImportTemplateModal from "./ImportTemplateModal.vue";
import { useI18n } from "vue-i18n";
import { DialogTitle } from "@headlessui/vue";
import { ChevronRightIcon } from "lucide-vue-next";

defineEmits(["use"]);

const props = defineProps({
    showClassic: {
        type: Boolean,
        default: true,
    },
    showAdvanced: {
        type: Boolean,
        default: true,
    },
});

const { t: __ } = useI18n();

const modal = ref();
const importModal = ref();

const store = useStore();

const show = () => {
    modal.value?.show();
};

const hide = () => {
    modal.value?.hide();
};

const filteredTemplates = computed(() => {
    if (!store.isLoadedData) {
        return [];
    }

    return store.templates.filter((template) => {
        if (!props.showClassic && template.payload.modules.content.automatic.enabled) {
            return false;
        }

        if (!props.showAdvanced && !template.payload.modules.content.automatic.enabled) {
            return false;
        }

        return true;
    });
});

defineExpose({ show, hide });
</script>
<template>
    <Modal ref="modal" class="overflow-hidden sm:max-w-2xl">
        <div class="max-h-[80vh] flex flex-col">
            <div class="flex flex-none justify-between items-center px-4 py-4 space-x-3 border-b border-gray-300 dark:border-gray-800 lg:px-8 sm:px-6">
                <DialogTitle class="text-lg font-semibold leading-6 text-gray-900 dark:text-white">{{ __("Templates") }}</DialogTitle>
                <PrimaryButton @click="importModal.show()" :z="1">
                    {{ __("Importer un template") }}
                </PrimaryButton>
            </div>

            <div class="min-h-0 flex flex-col flex-1">
                <div class="overflow-y-auto flex-1">
                    <div v-if="!store.isLoadedData" class="flex justify-center items-center px-4 py-4 lg:px-8 sm:px-6">
                        <LoadingAnimation class="w-20 h-20" />
                    </div>
                    <table v-else-if="filteredTemplates.length > 0" class="w-full text-left whitespace-nowrap">
                        <thead class="sticky top-0 bg-gray-100 dark:bg-gray-800">
                            <tr>
                                <th class="py-4 pr-8 pl-4 text-sm text-gray-900 dark:text-white lg:pl-8 sm:pl-6">
                                    {{ __("Nom") }}
                                </th>
                                <th class="py-4 pr-8 pl-0 text-sm text-gray-900 dark:text-white">
                                    {{ __("Mode") }}
                                </th>
                                <th class="py-4 pr-4 pl-0 text-right lg:pr-8 sm:pr-6"></th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-300 dark:divide-gray-800">
                            <tr
                                v-for="template in filteredTemplates"
                                :key="template.id"
                                @click="$emit('use', template)"
                                class="cursor-pointer dark:hover:bg-gray-800 hover:bg-gray-100">
                                <td class="w-full py-4 pr-8 pl-4 lg:pl-8 sm:pl-6">
                                    <div class="flex flex-col gap-y-1">
                                        <div class="text-sm font-medium leading-6 text-gray-900 truncate dark:text-white">{{ template.name }}</div>
                                        <div class="mt-0.5 text-xs italic text-gray-600 dark:text-gray-400">v{{ template.plugin_version }}</div>
                                    </div>
                                </td>
                                <td class="py-4 pr-8 pl-0 text-sm text-gray-600 dark:text-gray-400">
                                    {{ template.payload.modules.content.automatic.enabled ? __("Classique") : __("Avancé") }}
                                </td>
                                <td class="py-4 pr-4 pl-0 text-right lg:pr-8 sm:pr-6">
                                    <div class="flex justify-end items-center space-x-3">
                                        <ChevronRightIcon class="w-5 h-5 text-gray-600 dark:text-gray-400" aria-hidden="true" />
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-else class="flex justify-center items-center px-4 py-4 lg:px-8 sm:px-6">
                        <div class="text-sm text-gray-600 dark:text-gray-400">{{ __("Aucun template trouvé.") }}</div>
                    </div>
                </div>
            </div>
        </div>

        <ImportTemplateModal ref="importModal" />
    </Modal>
</template>
